"use client";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Breadcrumb from "@/components/Breadcrumbs/Breadcrumb";
import InputField from "@/components/Inputs/InputField";
import TextAreaField from "@/components/Inputs/TextAreaField";
import { usePatente } from "./hooks/usePatente";
import { Controller, useForm, useWatch } from "react-hook-form";
import { IoIosSearch } from "react-icons/io";
import { DateTime } from "luxon";
import { FaRegAddressCard } from "react-icons/fa";
import { upperCase } from "lodash";
import { useDialog } from "@/hooks/useDialog";
import Toast from "@/components/Toast/Toast";
import { useAuth } from "@/contexts/AuthContext";
import RadioGroup from "@/components/RadioGroup/RadioGroup";
import AltchaGate from "@/components/Altcha/AltchaGate";

const ConsultaPatente = () => {
  const { user } = useAuth();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [altchaKey, setAltchaKey] = useState(0);
  const { show } = useDialog();
  const [mode, setMode] = useState("consulta");

  const { control, handleSubmit, setValue } = useForm();

  const { info, isLoading, getPatente } = usePatente();

  const estado = useWatch({
    control,
    name: "fechaSuspension",
    defaultValue: "",
  });

  const tipoPersona = useWatch({
    control,
    name: "tipoPersona",
    defaultValue: "natural",
  });

  const resetAltcha = useCallback(() => {
    setCaptchaToken(null);
    setAltchaKey((prev) => prev + 1);
  }, []);

  const onSubmit = async (data) => {
    if (!user && !captchaToken) {
      show({
        type: "warning",
        title: "Validación requerida",
        message: "Debe realizar la validación.",
      });
      return;
    }

    if (data?.busqueda != null && data?.busqueda.length > 9) {
      const tokenToUse = user ? null : captchaToken;
      await getPatente(data.busqueda, tokenToUse);

      if (!user) {
        resetAltcha();
      }

      setMode("visualizacion");
    } else {
      show({
        type: "warning",
        title: "Datos incorrectos",
        message: "Debe ingresar un número de cédula o RUC válido",
      });
    }
  };

  function formatearFechas(obj) {
    const resultado = { ...obj };

    Object.keys(obj).forEach((key) => {
      if (key.toLowerCase().startsWith("fecha")) {
        const valor = obj[key];

        const fechaValida =
          valor && valor !== "0001-01-01T00:00:00"
            ? DateTime.fromISO(valor)
            : null;

        resultado[key] =
          fechaValida && fechaValida.isValid
            ? fechaValida.toFormat("dd/MM/yyyy hh:mm:ss a").toLowerCase()
            : "-";
      }
    });

    return resultado;
  }

  const limpiar = useCallback(() => {
    setValue("busqueda", "");
    setValue("tipoPersona", "natural");
    setValue("ruc", "");
    setValue("tipoContribuyente", "");
    setValue("obligadoContabilidad", "");
    setValue("razonSocial", "");
    setValue("actividadPrincipal", "");
    setValue("fechaInicio", "");
    setValue("fechaActualizacion", "");
    setValue("fechaSuspension", "");
    setValue("calificacionArtesanal", "");
    setValue("ramaArtesanal", "");
    setValue("numeroArtesano", "");
    setValue("fechaTitulacionArtesanal", "");
    setValue("fechaCalificacionArtesanal", "");
    setValue("fechaCaducidadArtesanal", "");
  }, [setValue]);

  useEffect(() => {
    limpiar();

    if (info != null) {
      const formatInfo = formatearFechas(info);

      let actividad = "";
      if (formatInfo?.Locales?.length > 0) {
        actividad = formatInfo?.Locales[0]?.actividades?.[0]?.descripcion ?? "-";
      } else {
        actividad = formatInfo?.OtrasActividades?.[0]?.descripcion ?? "-";
      }

      setValue("ruc", formatInfo.cedularuc);
      setValue("obligadoContabilidad", formatInfo.contabilidad ? "SI" : "NO");
      setValue("razonSocial", formatInfo.propietario);
      setValue("actividadPrincipal", upperCase(actividad));
      setValue("fechaInicio", formatInfo.fechainicio);
      setValue("fechaActualizacion", formatInfo.fechaactualizacion);
      setValue("fechaSuspension", formatInfo.fechacierre);

      setValue(
        "calificacionArtesanal",
        formatInfo.CalificacionArtesanal !== ""
          ? formatInfo.CalificacionArtesanal
          : "No Aplica"
      );
      setValue(
        "ramaArtesanal",
        formatInfo.ramaartesanal !== "" ? formatInfo.ramaartesanal : "No Aplica"
      );
      setValue(
        "numeroArtesano",
        formatInfo.numeroartesano !== "0"
          ? formatInfo.numeroartesano
          : "No Aplica"
      );

      setValue("fechaTitulacionArtesanal", formatInfo.fechatitulacionartesanal);
      setValue("fechaCalificacionArtesanal", formatInfo.fechacalificacionartesanal);
      setValue("fechaCaducidadArtesanal", formatInfo.fechacaducidadartesanal);
    }
  }, [info, limpiar, setValue]);

  const handleMode = () => {
    limpiar();
    resetAltcha();
    setMode("consulta");
  };

  const placeholder = useMemo(() => {
    return tipoPersona === "natural"
      ? "Ingrese el número de cédula a consultar"
      : "Ingrese el RUC a consultar";
  }, [tipoPersona]);

  return (
    <>
      <Breadcrumb pageName="Consulta de Patente" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-8">
          {mode === "consulta" && (
            <div className="flex flex-col place-items-center gap-4 border px-16 py-8 shadow">
              <div className="flex flex-col gap-4">
                <RadioGroup
                  name="tipoPersona"
                  control={control}
                  label="Tipo de Persona"
                  options={[
                    { label: "Natural", value: "natural" },
                    { label: "Jurídica", value: "juridica" },
                  ]}
                />

                <Controller
                  name="busqueda"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!/^\d+$/.test(value)) {
                        return "Solo se permiten números.";
                      }

                      if (tipoPersona === "natural" && value.length !== 10) {
                        return "La cédula debe tener 10 caracteres.";
                      }

                      if (tipoPersona === "juridica" && value.length !== 13) {
                        return "El RUC debe tener 13 caracteres.";
                      }

                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Identificación
                        </label>

                        <div className="flex w-125 items-center rounded-lg border border-gray-300 bg-white px-3 py-2">
                          <FaRegAddressCard />
                          <input
                            {...field}
                            id="busqueda"
                            placeholder={placeholder}
                            type="text"
                            className="peer ml-2 w-full flex-1 bg-transparent outline-none"
                          />
                        </div>

                        {fieldState.error && (
                          <span className="mt-1 text-sm text-red-500">
                            {fieldState.error.message}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || (!user && !captchaToken)}
                className="text-md flex w-fit items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <IoIosSearch />
                Buscar
              </button>

              {user == null && (
                <AltchaGate
                  key={altchaKey}
                  className="flex justify-center"
                  onToken={setCaptchaToken}
                />
              )}
            </div>
          )}

          {!isLoading && mode === "visualizacion" && (
            <button
              type="button"
              className="text-md flex w-fit items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-blue-900"
              onClick={handleMode}
            >
              <IoIosSearch />
              Nueva consulta
            </button>
          )}

          {isLoading && (
            <div className="flex gap-4">
              <div className="loading"></div>
              Cargando Información...
            </div>
          )}

          <Toast
            visible={!isLoading && info != null && mode === "visualizacion"}
            type="info"
            message={`El estado de la patente municipal del contribuyente es: ${estado != "-" ? "INACTIVO" : "ACTIVO"}`}
          />

          {!isLoading && info != null && mode === "visualizacion" && (
            <div className="flex flex-col gap-8">
              <div className="font-bold text-slate-400">
                Informacion del Contribuyente
              </div>

              <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputField
                  id="ruc"
                  label="Número de identidad"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="obligadoContabilidad"
                  label="Obligado a llevar contabilidad"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="razonSocial"
                  label="Razón Social"
                  disabled={true}
                  control={control}
                  className="md:col-span-2 xl:col-span-3"
                />
              </div>

              <div className="font-bold text-slate-400">
                Actividad Ecónomica
              </div>

              <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 xl:grid-cols-3">
                <TextAreaField
                  id="actividadPrincipal"
                  label="Actividad Principal"
                  disabled={true}
                  control={control}
                  className="md:col-span-2 xl:col-span-3"
                  rows={2}
                />
                <InputField
                  id="fechaInicio"
                  label="Fecha de inicio de actividades (en Portoviejo)"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="fechaActualizacion"
                  label="Fecha de actualización"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="fechaSuspension"
                  label="Fecha de suspensión"
                  disabled={true}
                  control={control}
                />
              </div>

              <div className="font-bold text-slate-400">
                Información Artesanal
              </div>

              <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 xl:grid-cols-3">
                <InputField
                  id="calificacionArtesanal"
                  label="Calificación artesanal"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="ramaArtesanal"
                  label="Rama artesanal"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="numeroArtesano"
                  label="Número Artesano"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="fechaTitulacionArtesanal"
                  label="Fecha Titulación Artesanal"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="fechaCalificacionArtesanal"
                  label="Fecha Calificación Artesanal"
                  disabled={true}
                  control={control}
                />
                <InputField
                  id="fechaCaducidadArtesanal"
                  label="Fecha Caducidad Artesanal"
                  disabled={true}
                  control={control}
                />
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default ConsultaPatente;